import { createContext, useContext, useEffect, useState, type Context } from 'react';

import memoizeOne from 'memoize-one';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { me } from '../../services/me';
import { type ProfileData } from './types';

const fetchProfileData = memoizeOne(async (): Promise<ProfileData> => {
	const data = await me();

	return {
		aaid: data.account_id,
		name: data.name || '',
		displayName: data.nickname,
		avatarUrl: data.picture,
		email: data.email,
	};
});

/**
 * Resolves the current account id without mounting `useProfileController`. Shares the memoized
 * profile request, so calling it from an event handler never costs a second `/me` call.
 */
export const fetchProfileAaid = async (): Promise<string | undefined> =>
	(await fetchProfileData()).aaid;

export const ProfileContext: Context<ProfileData | null> = createContext<ProfileData | null>(null);

const emptyProfileData: ProfileData = {
	name: '',
	displayName: '',
	avatarUrl: '',
};

type ProfileStatus = 'loading' | 'ready' | 'unavailable';

type ProfileControllerData = ProfileData & {
	profileStatus?: ProfileStatus;
};

const toProfileControllerData = (
	profileData: ProfileData,
	profileStatus: ProfileStatus,
	isProfileStatusEnabled: boolean,
): ProfileControllerData =>
	isProfileStatusEnabled ? { ...profileData, profileStatus } : profileData;

export const useProfileController = (
	preFetchedProfileData?: ProfileData,
): ProfileControllerData => {
	const profileContextData = useContext(ProfileContext);
	const isProfileStatusEnabled = fg('rovo-chat-ai-inbox');
	const [profileData, setProfileData] = useState<ProfileControllerData>(() =>
		toProfileControllerData(emptyProfileData, 'loading', isProfileStatusEnabled),
	);

	useEffect(() => {
		if (preFetchedProfileData) {
			setProfileData(
				toProfileControllerData(
					preFetchedProfileData,
					preFetchedProfileData.aaid ? 'ready' : 'unavailable',
					isProfileStatusEnabled,
				),
			);
		} else if (profileContextData) {
			setProfileData(
				toProfileControllerData(
					profileContextData,
					profileContextData.aaid ? 'ready' : 'unavailable',
					isProfileStatusEnabled,
				),
			);
		} else {
			fetchProfileData()
				.then((data) =>
					setProfileData(
						toProfileControllerData(
							data,
							data.aaid ? 'ready' : 'unavailable',
							isProfileStatusEnabled,
						),
					),
				)
				.catch(() =>
					setProfileData(
						toProfileControllerData(emptyProfileData, 'unavailable', isProfileStatusEnabled),
					),
				);
		}
	}, [setProfileData, isProfileStatusEnabled, profileContextData, preFetchedProfileData]);

	return profileData;
};
